import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

export default function SubscriptionsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/subscriptions');
      setItems(r.data?.subscriptions || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const doAction = async (url, payload, message) => {
    try {
      await api.post(url, payload);
      toast.success(message);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Subscriptions</h1>
        <p className="text-slate-500 text-sm mt-0.5">View and manage user package subscriptions and trials.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>All User Subscriptions</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Cycle</TableHead>
                <TableHead>Trial Ends</TableHead>
                <TableHead>Subscription Ends</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((sub) => (
                <TableRow key={sub.id}>
                  <TableCell>
                    <div className="font-medium text-slate-900">{sub.user_id}</div>
                    <div className="text-xs text-slate-500">{sub.package?.name || sub.package_code || 'Package'}</div>
                  </TableCell>
                  <TableCell>{sub.package?.name || sub.package_code || '-'}</TableCell>
                  <TableCell>
                    <Badge className={sub.status === 'active' ? 'bg-emerald-100 text-emerald-800' : sub.status === 'trial' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}>
                      {sub.status || 'unknown'}
                    </Badge>
                  </TableCell>
                  <TableCell>{sub.billing_cycle || '-'}</TableCell>
                  <TableCell>{formatDate(sub.trial_end)}</TableCell>
                  <TableCell>{formatDate(sub.subscription_end)}</TableCell>
                  <TableCell className="font-mono text-xs break-all">{sub.payment_reference || '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const days = Number(window.prompt('Extend trial by how many days?', '14') || 0);
                        if (days > 0) doAction(`/admin/users/${sub.user_id}/subscription/trial`, { days }, 'Trial extended');
                      }}>
                        Extend Trial
                      </Button>
                      <Button type="button" variant="outline" size="sm" onClick={() => {
                        const days = Number(window.prompt('Extend subscription by how many days?', '30') || 0);
                        if (days > 0) doAction(`/admin/users/${sub.user_id}/subscription/extend`, { days }, 'Subscription extended');
                      }}>
                        Extend
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => doAction(`/admin/users/${sub.user_id}/subscription/cancel`, {}, 'Subscription cancelled')}>
                        Cancel
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
